import React from 'react';
import { useLocale } from 'locale';
import { IDeveloper, MarketplaceType } from 'utils/entities';
import { Text } from 'ds/components/typography';
import { TransformedImage } from 'components/transformed-image';
import DeveloperLink from './DeveloperLink';
import {
  Root,
  DeveloperWrapper,
  DeveloperLogo,
  DeveloperInfo,
  DeveloperMainInfo,
  EstablishmentYear,
  DeveloperLinks,
  DeveloperLinkInner,
} from './styled';
import { marketplaceSelector } from 'store/state/selectors/router';
import { useSelector } from 'react-redux';
import { developerIntegrationLevelToText } from 'store/sagas/analytics/property';
import SuitcaseIcon from 'assets/svg/developer/suitcase.svg';
import ArrowIcon from 'assets/svg/arrow-chevron-left.svg';

interface DevelopersProps {
  developers: IDeveloper[];
  hasDescription: boolean;
}

export const Developers: React.FC<DevelopersProps> = props => {
  const { developers, hasDescription } = props;

  return (
    <Root data-auto="developers">
      {developers.map(developer => (
        <Developer
          key={developer.id}
          developer={developer}
          hasDescription={hasDescription}
        />
      ))}
    </Root>
  );
};

const LOGO_HEIGHT = 48;
const LOGO_DENSITY = 2;
export const DEVELOPER_LOGO_HEIGHT = LOGO_HEIGHT * LOGO_DENSITY;

interface DeveloperProps {
  developer: IDeveloper;
  hasDescription: boolean;
}

const Developer: React.FC<DeveloperProps> = props => {
  const { developer, hasDescription } = props;
  const { t } = useLocale();
  const hasLink = Boolean(developer.developerLink);
  const marketplace = useSelector(marketplaceSelector);
  const isCommercial = marketplace === MarketplaceType.Commercial;

  return (
    <DeveloperWrapper hasLink={hasLink} hasDescription={hasDescription} isCommercial={isCommercial}>
      <DeveloperLink id={developer.id} link={developer.developerLink}>
        <DeveloperLogo data-auto="developer-image-container">
          {developerIntegrationLevelToText[developer.integrationLevel] !== 'free' && developer.logoPath ? (
            <TransformedImage path={developer.logoPath} height={DEVELOPER_LOGO_HEIGHT} />
          ) : <SuitcaseIcon width={38} />}
        </DeveloperLogo>
      </DeveloperLink>
      <DeveloperInfo>
        <DeveloperMainInfo>
          <DeveloperLink id={developer.id} link={developer.developerLink}>
            <Text weight="bold" data-auto="developer-name">{developer.name}</Text>
          </DeveloperLink>
          {developer.establishmentYear ? <EstablishmentYear>{t('projectPage.developerEstablishment', { year: developer.establishmentYear })}</EstablishmentYear> : null}
        </DeveloperMainInfo>
        <DeveloperLinks>
          {hasLink ? (
            <DeveloperLink id={developer.id} link={developer.developerLink}>
              <DeveloperLinkInner>
                <Text weight="medium">{t('projectPage.aboutDeveloperLink')}</Text>
                <ArrowIcon />
              </DeveloperLinkInner>
            </DeveloperLink>
          ) : null}
        </DeveloperLinks>
      </DeveloperInfo>
    </DeveloperWrapper>
  );
};
